import { KeyValuePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import moment from 'moment-timezone';

import { DemoDateTimeFields } from '@demo/data/demo-cipo-fields';
import { CipoDateTimeComponent } from '@shared/components/fields/cipo-date-time/cipo-date-time.component';
import { CipoDateTimeControl, CipoDateTimePickerType } from '@shared/components/fields/common';
import { DATE_TIME_FORMATS } from '@shared/consts';
import { UserStore } from '@signal-store/user.store';

@Component({
  selector: 'app-demo-cipo-date-time',
  imports: [CipoDateTimeComponent, FlexLayoutModule, KeyValuePipe],
  templateUrl: './demo-cipo-date-time.component.html',
})
export class DemoCipoDateTimeComponent {
  private userStore = inject(UserStore);

  form = signal(new FormGroup<{ [x: string]: CipoDateTimeControl }>({}));
  lastValue = signal<string | string[]>(null);
  lastValuePickerType = signal<CipoDateTimePickerType>(null);

  timezone = computed(() => this.userStore.userData().ianaTimeZoneId);
  timezoneOffsetHours = computed(() => moment.tz(new Date(), this.timezone()).utcOffset() / 60);

  browserTimezone = computed(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  browserTimezoneOffsetHours = computed(() => moment(new Date()).utcOffset() / 60);

  lastValuePickerTypeFormat = computed(() => {
    switch (this.lastValuePickerType()) {
      case 'date':
        return DATE_TIME_FORMATS.date;
      case 'time':
        return DATE_TIME_FORMATS.time;
      case 'dateTime':
        return DATE_TIME_FORMATS.dateTime;
      default:
        return null;
    }
  });

  lastValueInBrowserTimezone = computed(() => {
    const val = this.lastValue();
    if (!val) {
      return null;
    }
    if (Array.isArray(val)) {
      return val
        .map(v =>
          moment
            .utc(v)
            .add(this.lastValuePickerType() === 'date' ? 0 : this.browserTimezoneOffsetHours(), 'hours')
            .format(this.lastValuePickerTypeFormat()),
        )
        .join(',');
    }
    return moment
      .utc(val)
      .add(this.lastValuePickerType() === 'date' ? 0 : this.browserTimezoneOffsetHours(), 'hours')
      .format(this.lastValuePickerTypeFormat());
  });

  constructor() {
    DemoDateTimeFields.forEach(field => {
      let { name, defaultValue, ...props } = field;
      if (name == '22date') {
        defaultValue = moment.tz(new Date(), this.timezone()).add(2, 'days').format(DATE_TIME_FORMATS.dateIso);
      }
      if (props.minDate) {
        const dt = moment.tz(new Date(), this.timezone());
        props.minDate = props.pickerType === 'date' ? dt.format(DATE_TIME_FORMATS.dateIso) : dt.toISOString();
      }
      if (props.maxDate) {
        const dt = moment.tz(new Date(), this.timezone());
        props.maxDate = props.pickerType === 'date' ? dt.format(DATE_TIME_FORMATS.dateIso) : dt.toISOString();
      }
      const control = new CipoDateTimeControl(defaultValue, props);

      control.valueChanges.subscribe(value => {
        this.lastValue.set(value);
        this.lastValuePickerType.set(control.fieldData.pickerType);
      });

      this.form().addControl(name, control);
    });
  }
}
